import { Directive, ElementRef, Input, Renderer2, OnInit, OnDestroy, signal, effect } from '@angular/core';
import { SPIN_APPEND, SPIN_POSITION, SPIN_STYLES, SpinInputModel } from 'countable@model';

@Directive({
    selector: '[appSpin]',
    standalone: true
})
export class SpinDirective implements OnDestroy {
    private spinnerState = signal<SpinInputModel>(new SpinInputModel());
    private progress = 0;
    private progressInterval: any = null;
    private progressBarEl: HTMLElement = null;
    private rightSpinner: HTMLElement = null;
    private finalDelayTimeout: any = null;
    private overlayElement!: HTMLElement;
    private spinnerElement!: HTMLElement;


    @Input()
    set spinnerConfig(value: SpinInputModel) {
        this.spinnerState.set(value);
    }

    get spinnerConfig() {
        return this.spinnerState();
    }

    constructor(private el: ElementRef, private renderer: Renderer2) {
        effect(() => {
            this.updateView(); // Reacts to changes in spinnerState
        });
    }

    updateView() {
        const state = this.spinnerConfig;
        if (!state.isSpinShown) {
            if (state.spinnerStyles === SPIN_STYLES.BLUR_WITH_LINE || state.spinnerStyles === SPIN_STYLES.LINE || state.spinnerStyles === SPIN_STYLES.BLUR_WITH_SPIN_LINE ||
                state.spinnerStyles === SPIN_STYLES.BLUR_WITH_SPIN){
                if (this.finalDelayTimeout) {
                    clearTimeout(this.finalDelayTimeout);
                    this.finalDelayTimeout = null;
                }
                !(state.spinnerStyles === SPIN_STYLES.BLUR_WITH_SPIN) && this.setTopProgressBar(100);
                this.finalDelayTimeout = setTimeout(() => {
                    this.removeOverlay();
                }, state.delay);
            } else {
                this.removeOverlay();
            }
            return;
        }
        this.renderer.setStyle(this.el.nativeElement, 'pointerEvents', 'none');
        if (state.spinnerStyles === SPIN_STYLES.BLUR_WITH_SPIN) {
            !this.overlayElement && !document.querySelector('.spin-blur-wrapper') && this.createOverlay();
        } else if (state.spinnerStyles === SPIN_STYLES.BLUR_WITH_LINE || state.spinnerStyles === SPIN_STYLES.LINE || state.spinnerStyles === SPIN_STYLES.BLUR_WITH_SPIN_LINE) {
            this.startSimulatedProgress()
        } else if (state.spinnerStyles === SPIN_STYLES.SPIN && !this.rightSpinner) {
            this.addRightSideSpinner();
        }
    }



    ngOnDestroy() {
        this.removeOverlay();
    }

    private createOverlay() {
        const state = this.spinnerConfig;
        const host = state.appendTo === SPIN_APPEND.BODY ? document.body : this.el.nativeElement;

        // Create blur overlay
        this.overlayElement = this.renderer.createElement('div');
        this.renderer.addClass(this.overlayElement, 'spin-blur-wrapper');

        // Create spinner
        this.spinnerElement = this.renderer.createElement('div');
        this.renderer.addClass(this.spinnerElement, 'smooth-gradient-ring-m');

        // Append spinner to overlay
        this.renderer.appendChild(this.overlayElement, this.spinnerElement);

        // Ensure host is relatively positioned
        const position = getComputedStyle(host).position;
        if (position === 'static' || !position) {
            this.renderer.setStyle(host, 'position', 'relative');
        }

        // Append overlay to host
        this.renderer.insertBefore(host, this.overlayElement, host.firstChild);

        // Disable interaction with all children
        this.renderer.setStyle(host, 'pointerEvents', 'none');
        this.renderer.setStyle(this.overlayElement, 'pointerEvents', 'auto');
    }

    private startSimulatedProgress() {
        if (this.progressInterval) return;

        this.progress = 0;
        const usedNumbers = new Set<number>();
        this.progressInterval = setInterval(() => {
            if (this.progress < 70) {
                let newNumber: number | null = null;

                // Find a new random number between 0 and 70 that hasn't been used
                const remainingNumbers = Array.from({ length: 71 }, (_, i) => i).filter(n => !usedNumbers.has(n));

                if (remainingNumbers.length === 0) {
                    clearInterval(this.progressInterval); // No more unique numbers
                    return;
                }

                // Pick one randomly
                newNumber = remainingNumbers[Math.floor(Math.random() * remainingNumbers.length)];
                usedNumbers.add(newNumber);

                this.progress += newNumber;
                this.progress = Math.min(this.progress, 70); // Cap at 70
                this.setTopProgressBar(this.progress);
            }
        }, 100);

    }
    private setTopProgressBar(progress: number) {
        const clampedProgress = Math.min(Math.max(progress, 0), 100);
        if (!this.progressBarEl) {
            this.progressBarEl = this.renderer.createElement('div');
            this.renderer.addClass(this.progressBarEl, 'topLineGradient');
            this.renderer.appendChild(this.el.nativeElement, this.progressBarEl);
        }
        this.renderer.setStyle(this.progressBarEl, 'width', `${clampedProgress}%`);
    }

    private addRightSideSpinner() {
        // Create spinner wrapper
        this.rightSpinner = this.renderer.createElement('div');
        this.renderer.setStyle(this.rightSpinner, 'position', 'absolute');
        this.renderer.setStyle(this.rightSpinner, 'top', 'calc(50% - 10px)'); // Adjusted to avoid direct copying
        if (this.spinnerConfig?.spinPosition === SPIN_POSITION.RIGHT) {
            this.renderer.setStyle(this.rightSpinner, 'right', '5%');
        } else if (this.spinnerConfig?.spinPosition === SPIN_POSITION.LEFT) {
            this.renderer.setStyle(this.rightSpinner, 'left', '5%');
        } else {
            this.renderer.setStyle(this.rightSpinner, 'left', '50%');
        }

        this.renderer.setStyle(this.rightSpinner, 'transform', 'translateY(-50%)');
        this.renderer.setStyle(this.rightSpinner, 'margin-left', '8px'); // gap between host and spinner
        // this.renderer.setStyle(this.rightSpinner, 'z-index', '9999');

        // Create spinner element
        const spinner = this.renderer.createElement('div');
        this.renderer.addClass(spinner, 'smooth-gradient-ring-s');

        this.renderer.appendChild(this.rightSpinner, spinner);
        this.renderer.appendChild(this.el.nativeElement, this.rightSpinner);
    }

    private removeOverlay() {
        const state = this.spinnerConfig;
        const host = state.appendTo === SPIN_APPEND.BODY ? document.body : this.el.nativeElement;
        if (this.overlayElement) {
            this.renderer.removeChild(host, this.overlayElement);
            this.overlayElement = null;
        }
        // Restore interactions
        this.renderer.setStyle(this.el.nativeElement, 'pointerEvents', 'auto');
        if (this.progressBarEl) {
            this.renderer.removeChild(this.el.nativeElement, this.progressBarEl);
            this.progressBarEl = null;
        }
        if (this.rightSpinner) {
            this.renderer.removeChild(this.el.nativeElement, this.rightSpinner);
            this.rightSpinner = null;
        }
        if (this.finalDelayTimeout) {
            clearTimeout(this.finalDelayTimeout);
            this.finalDelayTimeout = null;
        }

        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    }
}
